import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { updateDoc } from 'firebase/firestore';
import {
  TruckIcon,
  MapPinIcon,
  MapIcon,
  ClockIcon,
  UsersIcon,
  BriefcaseIcon,
  FaceSmileIcon,
  SparklesIcon,
  PhoneIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

export default function BookRecap() {
  const { uid } = useParams();

  const [booking, setBooking] = useState(null);

  const [loading, setLoading] = useState(true);
  const [finalPrice, setFinalPrice] = useState(0);
  const [note, setNote] = useState('');

  useEffect(() => {
    const fetchBooking = async () => {
      const docSnap = await getDoc(doc(db, 'quotes', uid));
      if (docSnap.exists()) {
        const data = docSnap.data();
        setBooking(data);
        setFinalPrice(data.price); // Pre-imposta il prezzo stimato
      }
      setLoading(false);
    };
    fetchBooking();
  }, [uid]);

  const handleApprove = async () => {
    try {
      // 1. Chiamata alla Netlify Function per generare il link Stripe e inviare l'email
      const response = await fetch('/.netlify/functions/send-payment-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          //bookingId: uid,
          customerEmail: booking.email,
          customerName: `${booking.firstName} ${booking.lastName}`,
          finalPrice: finalPrice,
          note: note,
          //details: booking, // Passiamo tutto l'oggetto per l'email
        }),
      });

      if (response.ok) {
        // 2. Aggiorniamo lo stato su Firestore
        await updateDoc(doc(db, 'quotes', uid), {
          status: 'WAITING_PAYMENT',
          finalPrice: finalPrice,
          adminNote: note,
        });
      }
    } catch (error) {
      console.error('Errore:', error);
    }
  };

  const computeStatus = status => {
    switch (status) {
      case 'WAITING_PAYMENT':
        return (
          <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-blue-700">
            In attesa pagamento
          </span>
        );
      case 'PENDING':
        return (
          <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-amber-700">
            Nuova richiesta
          </span>
        );
      case 'PAID':
      case 'CONFIRMED':
        return (
          <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-green-700">
            Pagato
          </span>
        );
      case 'REJECTED':
        return (
          <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-red-700">
            Annullata
          </span>
        );
      case 'EXPIRED':
        return (
          <span className="rounded-full bg-gray-200 px-3 py-1 text-xs font-bold uppercase tracking-wider text-gray-600">
            Pagamento scaduto
          </span>
        );
      case 'COMPLETED':
        return (
          <span className="rounded-full bg-slate-800 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">
            Completato
          </span>
        );
      default:
        return (
          <span className="rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider">
            -
          </span>
        );
    }
  };

  if (loading) return <div className="p-10 text-center">Caricamento...</div>;
  if (!booking) return <div className="p-10 text-center">Prenotazione non trovata.</div>;
  return (
    <div className="min-h-screen bg-gray-50 p-4 font-sans md:p-8">
      <div className="mx-auto grid max-w-5xl grid-cols-1 gap-8 lg:grid-cols-3">
        {/* COLONNA SINISTRA: DETTAGLI CORSA */}
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {booking.firstName} {booking.lastName}
                </h1>
                <p className="text-sm text-gray-500">
                  {uid !== undefined ? uid.toLocaleLowerCase() : '-'}
                </p>
              </div>
              {computeStatus(booking?.status)}
            </div>

            {/* Tragitto */}
            <div className="mb-8 space-y-4">
              <div className="flex items-start gap-3">
                <MapPinIcon className="mt-1 size-8 text-green-600" />
                <div>
                  <p className="text-xs font-bold uppercase text-gray-400">Partenza</p>
                  <p className="font-medium text-gray-800">{booking.pickup}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapIcon className="mt-1 size-8 text-red-600" />
                <div>
                  <p className="text-xs font-bold uppercase text-gray-400">Destinazione</p>
                  <p className="font-medium text-gray-800">{booking.destination}</p>
                </div>
              </div>
            </div>

            {/* Grid Dettagli Tecnici */}
            <div className="grid grid-cols-2 gap-4 border-y border-gray-50 py-6 md:grid-cols-4">
              <div className="flex flex-col items-center rounded-xl bg-gray-50 p-3">
                <ClockIcon className="mb-1 size-8 text-amber-600" />
                <span className="text-xs uppercase text-gray-400">Orario</span>
                <span className="text-sm font-bold">
                  {booking.date} @ {booking.time}
                </span>
              </div>
              <div className="flex flex-col items-center rounded-xl bg-gray-50 p-3">
                <TruckIcon className="mb-1 size-8 text-amber-600" />
                <span className="text-xs uppercase text-gray-400">Veicolo</span>
                <span className="text-sm font-bold uppercase">{booking.vehicle}</span>
              </div>
              <div className="flex flex-col items-center rounded-xl bg-gray-50 p-3">
                <UsersIcon className="mb-1 size-8 text-amber-600" />
                <span className="text-xs uppercase text-gray-400">Pass.</span>
                <span className="text-sm font-bold">{booking.passengers.value}</span>
              </div>
              <div className="flex flex-col items-center rounded-xl bg-gray-50 p-3">
                <BriefcaseIcon className="mb-1 size-8 text-amber-600" />
                <span className="text-xs uppercase text-gray-400">Bagagli</span>
                <span className="text-sm font-bold">{booking.luggage.value}</span>
              </div>
            </div>

            {/* Servizi Extra */}
            <div className="mt-6">
              <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-gray-800">
                Servizi Aggiuntivi
              </h3>
              <div className="flex flex-wrap gap-3">
                {booking.countHighChair > 0 && (
                  <span className="flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700">
                    <FaceSmileIcon className="size-8" /> Seggiolino Auto (x{booking.countHighChair})
                  </span>
                )}
                {booking.countChair > 0 && (
                  <span className="flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700">
                    Rialzo (x{booking.countChair})
                  </span>
                )}
                {booking.countFlowers > 0 && (
                  <span className="flex items-center gap-2 rounded-lg bg-pink-50 px-3 py-2 text-sm font-medium text-pink-700">
                    <SparklesIcon className="size-8" /> Fiori (x{booking.countFlowers})
                  </span>
                )}
                {booking.extraStop && (
                  <span className="flex items-center gap-2 rounded-lg bg-purple-50 px-3 py-2 text-sm font-medium text-purple-700">
                    <MapIcon className="size-8" /> Extra Stop
                  </span>
                )}
              </div>
            </div>

            {/* Note Cliente */}
            <div className="mt-8 rounded-r-lg border-l-4 border-yellow-400 bg-yellow-50 p-4">
              <p className="mb-1 text-xs font-bold uppercase text-yellow-700">Note del cliente:</p>
              <p className="italic text-gray-700">{booking.notes || 'Nessuna nota inserita'}</p>
            </div>
          </div>
        </div>

        {/* COLONNA DESTRA: AZIONI ADMIN */}
        <div className="space-y-6">
          <div className="sticky top-8 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <h3 className="mb-6 text-lg font-bold text-gray-900">Pannello Approvazione</h3>

            {/* Contatti Rapidi */}
            <div className="mb-6 space-y-2 border-b pb-4">
              <a
                href={`tel:${booking.telehone}`}
                className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
              >
                <PhoneIcon className="size-8" /> {booking.telehone}
              </a>
              <a
                href={`mailto:${booking.email}`}
                className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
              >
                <EnvelopeIcon className="size-8" /> {booking.email}
              </a>
            </div>

            {/* Prezzo */}
            <div className="mb-6">
              <label
                htmlFor="final-price"
                className="mb-2 block text-sm font-bold uppercase italic text-gray-700"
              >
                Prezzo Finale da addebitare (€)
              </label>
              <input
                id="final-price"
                type="number"
                className="w-full rounded-xl border-2 border-gray-200 bg-gray-50 p-4 text-2xl font-black text-amber-600 outline-none transition-all focus:border-amber-500"
                value={finalPrice}
                onChange={e => setFinalPrice(e.target.value)}
              />
              <p className="mt-2 text-xs text-gray-400">Stima automatica: €{booking.price}</p>
            </div>

            {/* Nota di Andrei */}
            <div className="mb-6">
              <label
                htmlFor="notes"
                className="mb-2 block text-sm font-bold uppercase italic text-gray-700"
              >
                Messaggio per il cliente
              </label>
              <textarea
                id="notes"
                className="h-32 w-full rounded-xl border-2 border-gray-200 bg-gray-50 p-3 text-sm outline-none transition-all focus:border-amber-500"
                placeholder="Spiega eventuali variazioni di prezzo o aggiungi un benvenuto..."
                value={note}
                onChange={e => setNote(e.target.value)}
              />
            </div>

            <button
              onClick={handleApprove}
              className="w-full rounded-xl bg-black py-4 font-black text-white shadow-lg transition-transform hover:bg-gray-900 active:scale-95 disabled:bg-gray-300"
            >
              INVIA CONFERMA E LINK PAGAMENTO
            </button>
            <p className="mt-4 text-center text-[10px] uppercase tracking-tighter text-gray-400">
              L&apos;invio attiverà la Netlify Function e invierà l&apos;email tramite Resend.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
